import { ActivatedRoute } from '@angular/router';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { HabilitationType } from '../../../models/habilitation-type.model';
import { NavigationStep } from '../../../models/navigation-step.model';

@Component({
  selector: 'app-navegateur-formulaire',
  templateUrl: './navegateur-formulaire.component.html',
})
export class NavegateurFormulaireComponent implements OnInit, OnDestroy {

  navegateurData: {
    number: string;
    name: string;
  }[];

  structure: string;
  contrat: string;
  certificats: string;
  selectHabilitations: string;
  administrateur: string;
  recapitulatif: string;
  gestionnaires: string;
  mandataires: string;
  delegation: string;
  delegues: string;
  ars: string;

  @Input() idActive: number;
  @Input() isDuplication: boolean;
  @Input() isModifyDomaine: boolean;
  @Input() isDuplicateAllHabilitation: boolean;
  @Input() isDeleteAllHabilitation: boolean;
  @Input() habilitationType: string;
  @Output() navegateurEvent = new EventEmitter<NavigationStep>();

  isDuplicateSingleHabilitation: boolean;
  trigger = new BehaviorSubject<number>(0);
  subscription: Subscription;
  /* number of times to stop de subjectObsedrvable*/
  auxNumber = 0;

  constructor(
    private readonly translate: TranslateService,
    private readonly route: ActivatedRoute
  ) {
    this.route.queryParams.subscribe((params) => {
      this.isDuplicateSingleHabilitation = params.isDuplicateSingleHabilitation === 'true' ?? false;
      this.isDuplicateAllHabilitation = params.isDuplicateAllHabilitation === 'true' ?? false;
      this.isDeleteAllHabilitation = params.isDeleteAllHabilitation === 'true' ?? false;
    });
  }

  ngOnInit(): void {
    this.subscription = this.trigger
      .pipe(takeWhile((v) => v <= 1))
      .subscribe((v) => {
        if (v > 0) {
          this.navegateurData = this.evaluateNavegateurData();
        }
      });
    this.configTexts();
    this.navegateurData = this.evaluateNavegateurData();
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  configTexts() {
    this.translate.get('last').subscribe(async () => {
      this.structure = this.translate.instant('habilitation.navegateur.structure');
      this.certificats = this.translate.instant('habilitation.navegateur.certificats');
      this.selectHabilitations = this.translate.instant('habilitation.navegateur.selectHabilitations');
      this.administrateur = this.translate.instant('habilitation.navegateur.administrateurs');
      this.recapitulatif = this.translate.instant('habilitation.navegateur.recapitulatif');
      this.gestionnaires = this.translate.instant('habilitation.navegateur.gestionnaires');
      this.mandataires = this.translate.instant('habilitation.navegateur.mandataires');
      this.contrat = this.translate.instant('contrats.navegateur');
      this.delegation = this.translate.instant('habilitation.navegateur.delegation');
      this.delegues = this.translate.instant('habilitation.navegateur.delegues');
      this.ars = await this.translate.instant('habilitation.navegateur.ars');
      /* if this ars is populated the rest too*/
      if (this.ars) {
        this.auxNumber += 1;
        this.trigger.next(this.auxNumber);
      }
    });
  }

  evaluateNavegateurData(): { number: string; name: string }[] {
    this.configTexts();
    let ans: { number: string; name: string }[];
    switch (this.habilitationType) {
      case 'CL':
      case 'CL_PM':
        ans = this.caseCLType();
        break;
      case 'GAE':
      case 'GARS':
        if (this.isDuplicateAllHabilitation) {
          ans = [
            { number: '1', name: this.gestionnaires },
            { number: '2', name: this.recapitulatif },
          ];
        } else {
          ans = [
            { number: '1', name: this.habilitationType === 'GAE' ? this.structure : this.ars },
            { number: '2', name: this.gestionnaires },
            { number: '3', name: this.recapitulatif },
          ];
        }
        break;
      case 'DELEGUE':
      case 'DELEGUE_PM':
        ans = this.caseDelegue();
        break;
      case 'DELEGUE_GENERIQUE':
        ans = this.caseDelegueGenerique();
        break;
      case 'ADMIN_GENERIQUE':
        ans = this.caseAdminGenerique();
        break;
      case 'MANDATAIRE':
        ans = this.caseMandataire();
        break;
      case 'CONTRAT':
        ans = this.caseContrat('CONTRAT');
        break;
      case 'CONTRATUNIQUE':
        ans = this.caseContrat('CONTRATUNIQUE');
        break;
      case HabilitationType.CL_PM:
        ans = this.caseDesignerAdminTiers();
        break;
      default:
        console.log(`Problème lors de la configuration des etapes du Type = ${this.habilitationType}`);
        ans = null;
        break;
    }
    return ans;
  }

  private caseMandataire(): { number: string; name: string; }[] {
    return [
      { number: '1', name: this.structure },
      { number: '2', name: this.mandataires },
      { number: '3', name: this.recapitulatif },
    ];
  }

  private caseDelegue(): { number: string; name: string; }[] {
    return [
      { number: '1', name: this.structure ?? 'Structure' },
      { number: '2', name: this.delegation ?? 'Délégués' },
      { number: '3', name: this.recapitulatif ?? 'Recapitulatif' },
    ];
  }

  private caseDelegueGenerique(): { number: string; name: string; }[] {
    return [
      { number: '1', name: this.structure ?? 'Structure' },
      { number: '2', name: this.delegues ?? 'Délégués' },
      { number: '3', name: this.recapitulatif ?? 'Recapitulatif' },
    ];
  }

  private caseDesignerAdminTiers(): { number: string; name: string; }[] {
    return [
      { number: '1', name: this.structure },
      { number: '2', name: this.certificats },
      { number: '3', name: this.administrateur },
      { number: '4', name: this.recapitulatif },
    ];
  }

  private caseAdminGenerique(): { number: string; name: string; }[] {
    return [
      { number: '1', name: this.structure ?? 'Structure' },
      { number: '2', name: this.administrateur ?? 'Administrateurs' },
      { number: '3', name: this.recapitulatif ?? 'Recapitulatif' },
    ];
  }

  caseCLType(): any {
    let ans: any;
    if (this.isDeleteAllHabilitation) {
      ans = [
        { number: '1', name: this.selectHabilitations },
      ];
    } else if (this.isDuplication) {
      ans = [
        { number: '1', name: this.structure },
        { number: '2', name: this.recapitulatif },
      ];
    } else if (this.isDuplicateAllHabilitation) {
      ans = [
        { number: '1', name: this.selectHabilitations },
        { number: '2', name: this.administrateur },
        { number: '3', name: this.recapitulatif },
      ];
    } else if (this.isDuplicateSingleHabilitation) {
      ans = [
        { number: '1', name: this.administrateur },
        { number: '2', name: this.recapitulatif },
      ];
    } else {
      ans = this.isModifyDomaine
        ? [{ number: '1', name: this.recapitulatif }]
        : [
            { number: '1', name: this.structure },
            { number: '2', name: this.certificats },
            { number: '3', name: this.administrateur },
            { number: '4', name: this.recapitulatif },
          ];
    }
    return ans;
  }

  caseContrat(type: string): { number: string; name: string }[] {
    let ans: { number: string; name: string }[];
    switch (type) {
      case 'CONTRAT':
        ans = [
          { number: '1', name: this.contrat ?? 'Contrat' },
          { number: '2', name: this.structure ?? 'Structure' },
          { number: '3', name: this.recapitulatif ?? 'Recapitulatif' },
        ];
        break;
      case 'CONTRATUNIQUE':
        ans = [
          { number: '1', name: this.structure ?? 'Structure' },
          { number: '2', name: this.recapitulatif ?? 'Recapitulatif' },
        ];
        break;
    }
    return ans;
  }

}
